import React, { useMemo, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import useLocale from "@/hooks/useLocale";
import { getLanguagePath, getRouteType } from "@/routing";

import Button from "./Button";

const LanguageSwitcher = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = useLocale();

  const { t, i18n } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  // Get route type from pathname
  const routeType = useMemo(() => {
    return getRouteType(location.pathname);
  }, [location.pathname]);

  const isSurvey = useMemo(() => routeType === "survey", [routeType]);

  const handleLanguageSwitch = async (newLocale: string) => {
    if (locale === newLocale) {
      setIsOpen(false);
      return;
    }

    setIsOpen(false);

    // Change the language
    await i18n.changeLanguage(newLocale);

    // Navigate to the same route with new language while keeping the same query string
    const newPath = getLanguagePath(newLocale as "en" | "fr", routeType);
    navigate(newPath + location.search, { replace: true });
  };

  return (
    <>
      <div className="h-[58px] bg-[#f0f0f0]" />
      <div className="fixed bottom-0 z-10 h-[58px] w-full left-0">
        <div className={`w-[100%] max-w-[${isSurvey ? 834 : 640}px] mx-auto`}>
          <button
            className="text-left px-10 py-4 border-t border-t-[#979797] block w-full bg-[#f0f0f0] text-grey"
            onClick={() => setIsOpen(true)}
          >
            <img className="mr-3" src="/icons/planet.svg" alt="" />
            {t("common.languageSwitcherCta")}
          </button>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className={`w-[100%] max-w-[${isSurvey ? 834 : 640}px] left-1/2 -translate-x-1/2 fixed top-0 w-full h-screen bg-[#000]/80 z-20 text-white text-center p-10 flex items-center`}
              >
                <div className="w-full flex flex-col">
                  <button
                    className="text-0 ml-auto"
                    onClick={() => setIsOpen(false)}
                  >
                    <svg
                      width="25"
                      height="27"
                      viewBox="0 0 25 27"
                      fill="none"
                      stroke="#979797"
                      strokeWidth="2"
                      strokeLinecap="round"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M1.5 1.5L23.5 25.5" />
                      <path d="M23 2L1 26" />
                    </svg>
                    Close
                  </button>
                  <h3 className="text-xl mt-10">
                    {t("common.languageSwitcherTitle")}
                  </h3>
                  <ul className="mt-10 pb-10 justify-center text-sm font-medium">
                    <li>
                      <Button
                        onClick={() => handleLanguageSwitch("fr")}
                        className="font-semibold"
                      >
                        Français
                      </Button>
                    </li>
                    <li className="mt-2.5">
                      <Button
                        onClick={() => handleLanguageSwitch("en")}
                        className="font-semibold"
                      >
                        English
                      </Button>
                    </li>
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default LanguageSwitcher;
